import Vue from 'vue'
import Router from 'vue-router'
import store from '../store'
import server from '../store/server'
import { SET_PREVIOUS_ROUTE, SET_PERSONAL_MENU, SET_HELP_MENU_FLAG } from '../store/mutation-types'
import {GET_CURRENT_ACCESSIBILITY_FLAG, GET_ENGAGEMENT_LAST_SUCCESSFUL} from '../store/action-types'

// Lazy loaded pages
const MyStudentsPage = () => import('@/components/student/MyStudentsPage')
const LoginPage = () => import('@/components/login/LoginPage')
const LoginSSO = () => import('@/components/login/LoginSSO')
const DashboardPage = () => import('@/components/dashboard/DashboardPage')
const StudentPage = () => import('@/components/student/StudentPage')
const InteractionsPage = () => import('@/components/intervention/InteractionsPage')
const DailyFeedsPage = () => import('@/components/monitor/DailyFeedsPage')
const FeedRequestBreakdownInfo = () => import('@/components/monitor/FeedRequestBreakdownInfo')
const FeedErrorBreakdownDetails = () => import('@/components/monitor/FeedErrorBreakdownDetails')
const InsightsPage = () => import('@/components/insights/InsightsPage')
const HelpPage = () => import('@/components/help/HelpPage')
const AnnouncementEditPage = () => import('@/components/announcement/edit/AnnouncementEditPage')
const SupportPage = () => import('@/components/help/SupportPage')
const DataPurgePage = () => import('@/components/data-purge/DataPurgePage')
const AlertPage = () => import('@/components/alert/AlertPage')
const AlertDetailPage = () => import('@/components/alert/AlertDetailPage')
const ConfigPage = () => import('@/components/config/ConfigPage')
const ErrorPage = () => import('@/components/ErrorPage')

Vue.use(Router)

const shouldntBeLoggedIn = function (to, from, next) {
  if (store.state.profile.loggedIn) {
    if (store.getters.IS_STUDENT) {
      next(store.getters.IS_ACCESSIBLE ? '/studentAccessible' : '/student')
    } else {
      next('/dashboard')
    }
  } else if (store.state.config.useSSO || store.state.config.useSAML) {
    window.stop()
    server.removeAuthOnPage()
    window.location.replace(store.state.config.authUrlLogOut)
  } else {
    next()
  }
}

const isLoggedOn = function (next) {
  if (store.state.config.useSSO && !store.state.profile.loggedIn) {
    window.location.replace(store.state.config.authUrl)
    return false
  } else if (!store.state.profile.loggedIn) {
    next('/login')
    return false
  }
  return true
}

const studentCheck = function (next, isStudent, redirect) {
  if (isLoggedOn(next)) {
    if (store.getters.IS_STUDENT === isStudent) {
      next()
    } else {
      next(redirect)
    }
  }
}

/* const notAStudent = function (to, from, next) {
  studentCheck(next, false, '/student')
} */

const onlyAStudent = function (to, from, next) {
  studentCheck(next, true, '/dashboard')
}

const onlyAStudentAndInteractionsCheck = function (to, from, next) {
  if (store.getters.HAS_FEATURE_FLAG(store.state.profile.module.interexem + '.' + store.state.student.functions.studentInteractions + '.' + store.state.student.options.enable.interactionsTab)) {
    studentCheck(next, true, '/dashboard')
  } else {
    next('/dashboard')
  }
}

const onlyAStudentAndModulesCheck = function (to, from, next) {
  if (store.getters.HAS_FEATURE_FLAG(store.state.profile.module.studentmods + '.' + store.state.student.functions.studentModules + '.' + store.state.student.options.enable.modulesTab)) {
    studentCheck(next, true, '/dashboard')
  } else {
    next('/dashboard')
  }
}

const adminOrStaff = function (to, from, next) {
  if (isLoggedOn(next)) {
    if (store.getters.IS_ADMIN || store.getters.IS_STAFF) {
      next()
    } else if (store.getters.IS_STUDENT && store.getters.IS_ACCESSIBLE) {
      next('/studentAccessible')
    } else {
      next('/student')
    }
  }
}

const adminOrStaffWithInsightsAccess = function (to, from, next) {
  if (isLoggedOn(next)) {
    if (store.getters.IS_ADMIN) {
      next()
      return
    }
    if (store.getters.IS_STUDENT) {
      next(store.getters.IS_ACCESSIBLE ? '/studentAccessible' : '/student')
      return
    }
    if (store.getters.HAS_FEATURE_FLAG(`${store.state.profile.module.insights}.${store.state.profile.functions.headerMenu}.${store.state.profile.options.enable.insightsForStaff}`)) {
      next()
      return
    }
    next('/dashboard')
  }
}

const adminOrStaffOrStudent = function (to, from, next) {
  if (isLoggedOn(next)) {
    if (store.getters.IS_ADMIN || store.getters.IS_STAFF || store.getters.IS_STUDENT) {
      next()
    }
  }
}

const adminOrStaffAndInteractionAccess = function (to, from, next) {
  let flagEnableInteractions = store.getters.HAS_FEATURE_FLAG(store.state.profile.module.interexem + '.' + store.state.profile.functions.headerMenu + '.' + store.state.profile.options.enable.interactions)
  if (isLoggedOn(next)) {
    if (store.getters.IS_ADMIN || store.getters.IS_STAFF) {
      if (flagEnableInteractions) {
        next()
      } else {
        next('/dashboard')
      }
    } else if (store.getters.IS_STUDENT && store.getters.IS_ACCESSIBLE) {
      next('/studentAccessible')
    } else {
      next('/student')
    }
  }
}

const onlyAdmin = function (to, from, next) {
  if (isLoggedOn(next)) {
    if (store.getters.IS_ADMIN) {
      next()
    } else if (store.getters.IS_STUDENT) {
      next(store.getters.IS_ACCESSIBLE ? '/studentAccessible' : '/student')
    } else {
      next('/dashboard')
    }
  }
}

const onlyAdminAndAlertAccess = function (to, from, next) {
  let flagEnableAlerts = store.getters.HAS_FEATURE_FLAG(store.state.profile.module.alert + '.' + store.state.profile.functions.adminMenu + '.' + store.state.profile.options.enable.alerts)
  if (isLoggedOn(next)) {
    if (store.getters.IS_ADMIN && flagEnableAlerts) {
      next()
    } else if (store.getters.IS_STUDENT) {
      next(store.getters.IS_ACCESSIBLE ? '/studentAccessible' : '/student')
    } else {
      next('/dashboard')
    }
  }
}

const onlyAdminAndConfigAccess = function (to, from, next) {
  let flagConfigItemsExist =
    store.getters.HAS_FEATURE_FLAG(store.state.profile.module.config + '.' + store.state.configUI.functions.academic + '.' + store.state.configUI.options.enable.academic) ||
  store.getters.HAS_FEATURE_FLAG(store.state.profile.module.config + '.' + store.state.configUI.functions.intervention + '.' + store.state.configUI.options.enable.intervention) ||
  store.getters.HAS_FEATURE_FLAG(store.state.profile.module.config + '.' + store.state.configUI.functions.exemption + '.' + store.state.configUI.options.enable.exemption)
  let flagEnableConfigMenu = store.getters.HAS_FEATURE_FLAG(store.state.profile.module.config + '.' + store.state.profile.functions.adminMenu + '.' + store.state.profile.options.enable.config)
  if (isLoggedOn(next)) {
    if (store.getters.IS_ADMIN && flagConfigItemsExist && flagEnableConfigMenu) {
      next()
    } else {
      next('/dashboard')
    }
  }
}

const onlyAdminAndPurgeAccess = function (to, from, next) {
  let flagEnableStudentDataPurge = store.getters.HAS_FEATURE_FLAG(store.state.profile.module.sdpurge + '.' + store.state.profile.functions.sdpurgeMenu + '.' + store.state.profile.options.enable.sdpMenu)
  if (isLoggedOn(next)) {
    if (store.getters.IS_ADMIN && flagEnableStudentDataPurge) {
      next()
    } else {
      next('/dashboard')
    }
  }
}

const initialPage = function (to, from, next) {
  if (isLoggedOn(next)) {
    if (store.getters.IS_STUDENT) {
      next(store.getters.IS_ACCESSIBLE ? '/studentAccessible' : '/student')
    } else {
      next('/dashboard')
    }
  }
}

let router = new Router({
  mode: 'history',
  routes: [
    {
      path: '/login',
      name: 'Login',
      component: LoginPage,
      beforeEnter: shouldntBeLoggedIn
    },
    {
      path: '/loginsso',
      name: 'LoginSSO',
      component: LoginSSO
    },
    {
      path: '/dashboard',
      name: 'Dashboard',
      component: DashboardPage,
      beforeEnter: adminOrStaff
    },
    {
      path: '/students',
      name: 'Students',
      component: MyStudentsPage,
      beforeEnter: adminOrStaff
    },
    {
      path: '/interactions',
      name: 'Interactions',
      component: InteractionsPage,
      beforeEnter: adminOrStaffAndInteractionAccess
    },
    {
      path: '/student/:id',
      name: 'Student',
      component: StudentPage,
      beforeEnter: adminOrStaff
    },
    {
      path: '/student/:id/intervention/:intervention_id',
      name: 'TutorIntervention',
      component: StudentPage,
      beforeEnter: adminOrStaff
    },
    {
      path: '/student/:id/interactions',
      name: 'TutorInteractions',
      component: StudentPage,
      beforeEnter: adminOrStaff
    },
    {
      path: '/student/:id/attendance',
      name: 'TutorAttendance',
      component: StudentPage,
      beforeEnter: adminOrStaff
    },
    {
      path: '/studentAccessible',
      name: 'StudentView',
      redirect: to => '/view/profile',
      beforeEnter: onlyAStudent
    },
    {
      path: '/student',
      name: 'StudentHome',
      component: StudentPage,
      beforeEnter: onlyAStudent
    },
    {
      path: '/view/profile',
      name: 'StudentViewProfile',
      alias: '/view',
      component: () => import('../views/student/accessible/StudentViewProfile'),
      beforeEnter: onlyAStudent
    },
    {
      path: '/view/engagement',
      name: 'StudentViewEngagement',
      component: () => import('../views/student/accessible/StudentViewEngagement'),
      beforeEnter: onlyAStudent
    },
    {
      path: '/view/resources',
      name: 'StudentViewResources',
      component: () => import('../views/student/accessible/StudentViewResources'),
      beforeEnter: onlyAStudent
    },
    {
      path: '/view/interactions',
      name: 'StudentViewInteractions',
      component: () => import('../views/student/accessible/StudentViewInteractions'),
      beforeEnter: onlyAStudentAndInteractionsCheck
    },
    {
      path: '/view/modules',
      name: 'StudentViewModules',
      component: () => import('../views/student/accessible/StudentViewModules'),
      beforeEnter: onlyAStudentAndModulesCheck
    },
    {
      path: '/view/attendance',
      name: 'StudentViewAttendance',
      component: () => import('../views/student/accessible/StudentViewAttendance'),
      beforeEnter: onlyAStudent
    },
    {
      path: '/view/assessments',
      name: 'StudentViewAssessments',
      component: () => import('../views/student/accessible/StudentViewAssessments'),
      beforeEnter: onlyAStudent
    },
    {
      path: '/view/faqs',
      name: 'StudentViewFAQs',
      component: () => import('../views/student/accessible/StudentViewFAQs'),
      beforeEnter: onlyAStudent
    },
    {
      path: '/view/announcements',
      name: 'StudentViewAnnouncements',
      component: () => import('../views/student/accessible/StudentViewAnnouncements'),
      beforeEnter: onlyAStudent
    },
    {
      path: '/view/*',
      redirect: to => '/view/profile',
      beforeEnter: onlyAStudent
    },
    {
      path: '/student/:id/exemption/:exemption_id',
      name: 'TutorExemption',
      component: StudentPage,
      beforeEnter: adminOrStaff
    },
    {
      path: '/student/:id/exemption/:alert_id',
      name: 'TutorAlert',
      component: StudentPage,
      beforeEnter: adminOrStaff
    },
    {
      path: '/admin/alerts',
      name: 'AdminAlerts',
      component: AlertPage,
      beforeEnter: onlyAdminAndAlertAccess
    },
    {
      path: '/admin/alert/:id',
      name: 'AdminAlertDetail',
      component: AlertDetailPage,
      beforeEnter: onlyAdminAndAlertAccess
    },
    {
      path: '/admin/dailyfeeds',
      name: 'DailyFeeds',
      component: DailyFeedsPage,
      beforeEnter: onlyAdmin
    },
    {
      path: '/admin/feedbreakdowninfo/:id',
      name: 'FeedRequestBreakdownInfo',
      component: FeedRequestBreakdownInfo,
      beforeEnter: onlyAdmin
    },
    {
      path: '/admin/announcements',
      name: 'AdminAnnouncements',
      component: AnnouncementEditPage,
      beforeEnter: onlyAdmin
    },
    {
      path: '/insights',
      name: 'Insights',
      component: InsightsPage,
      beforeEnter: adminOrStaffWithInsightsAccess
    },
    {
      path: '/help/:index?',
      name: 'Help',
      component: HelpPage,
      beforeEnter: adminOrStaffOrStudent
    },
    {
      path: '/support/:id',
      name: 'Support',
      component: SupportPage,
      beforeEnter: adminOrStaffOrStudent
    },
    {
      path: '*',
      beforeEnter: initialPage
    },
    {
      path: '/error',
      name: 'Error',
      component: ErrorPage
    },
    {
      path: '/unauthorised',
      name: 'Unauthorised',
      component: ErrorPage
    },
    {
      path: '/feedErrorDetails/:auditId',
      name: 'FeedErrorBreakdownDetails',
      component: FeedErrorBreakdownDetails,
      beforeEnter: onlyAdmin
    },
    {
      path: '/admin/data-purge',
      name: 'AdminDataPurge',
      component: DataPurgePage,
      beforeEnter: onlyAdminAndPurgeAccess
    },
    {
      path: '/admin/config',
      name: 'AdminConfig',
      component: ConfigPage,
      beforeEnter: onlyAdminAndConfigAccess
    },
    {
      path: '/admin/config/:configId',
      name: 'AdminConfigDetail',
      component: ConfigPage,
      beforeEnter: onlyAdminAndConfigAccess
    }
  ]
})

router.beforeEach((to, from, next) => {
  store.commit(SET_PERSONAL_MENU, false)
  store.commit(SET_HELP_MENU_FLAG, false)
  store.commit(SET_PREVIOUS_ROUTE, from)
  if (!store.state.config.failed && store.state.profile.loginToken) {
    store.dispatch(GET_ENGAGEMENT_LAST_SUCCESSFUL)
  }
  if (!store.state.config.failed && store.state.profile.loginToken && store.getters.IS_STUDENT) {
    store.dispatch(GET_CURRENT_ACCESSIBILITY_FLAG)
  }
  next()
})
export default router



// WEBPACK FOOTER //
// ./src/router/index.js